import dynamic from "@redux-dynostore/react-redux";
import subspaced from "@redux-dynostore/react-redux-subspace";
import { attachReducer } from "@redux-dynostore/redux-subspace";
import React, { useCallback, useEffect, useState } from "react";
import { connect } from "react-redux";
import { sendNotification } from "../../ToastManager/ToastManager";
import { ToastType } from "../../ToastManager/constants";
import styled, { withTheme } from "styled-components";

import PieChart from "../../ui-library/PieChart/PieChart";
import HorizontalProgressBarChart from "../../ui-library/HorizontalProgressBarChart/HorizontalProgressBarChart";

import {
  dietDiaryChartsReducer,
  mapDispatchToProps,
  mapStateToProps,
} from "./DietDiaryCharts.store";
import moment from "moment";
import { DateFormat, kcalMultiplier } from "../../../modules/constants";
import { useTranslation } from "react-i18next";
import { roundMealValue } from "../../../helpers/mealsHelper";
import NutritionItem from "../../library/NutritionItem/NutritionItem";

const StyledPieChart = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
`;

const StyledHPBC = styled.div`
  margin-top: 20px;
`;

export const SmartDietDiaryCharts = (props) => {
  const {
    startDate,
    constants,
    getNutrientById,
    fetchMealsByDate,
    getMacronutrientColor,
    hpbcImpact,
    hpbcVals,
    setHpbcVals,
    existingDayMeals,
    calories,
    pieChartSeries,
    resetStates,
    participantId,
    mealId,
    studyId,
    theme,
    rerenderPieChart,
  } = props;

  const { t } = useTranslation();

  const [pieChartKey, setPieChartKey] = useState("pie-chart");

  useEffect(() => {
    if (rerenderPieChart) {
      setPieChartKey(moment().toISOString());
    }
  }, [rerenderPieChart]);

  const getMealImpactTitle = useCallback(() => {
    const date = moment(startDate).startOf("day");
    const today = moment(new Date()).startOf("day");

    if (date.isSame(today)) {
      return t("contentText.dietDiary.mealImpact.today.title");
    } else if (date.isBefore(today)) {
      const yesterday = today.subtract(1, "day").startOf("day");

      if (date.isSame(yesterday)) {
        return t("contentText.dietDiary.mealImpact.yesterday.title");
      }
    }

    return t("contentText.dietDiary.mealImpact.date.title", {
      date: date.format(DateFormat.BASE_FORMAT),
    });
  }, [startDate, t]);

  const getAboutMealImpactValue = useCallback(
    (searchObj, searchAttrId) => {
      const element = getNutrientById(searchAttrId);

      if (searchObj) {
        return searchObj[element?.aboutMealLetter]?.value ?? 0;
      }
      return 0;
    },
    [getNutrientById]
  );

  useEffect(() => {
    if (existingDayMeals) {
      const individualTotals = {
        protein: 0,
        carbs: 0,
        fats: 0,
        sugar: 0,
        calories: 0,
      };

      existingDayMeals.forEach((item) => {
        if (item.mealId !== mealId) {
          individualTotals.protein +=
            getAboutMealImpactValue(item.aboutMeal, constants.PROTEIN_ATTR_ID) *
            kcalMultiplier.protein;
          individualTotals.carbs +=
            getAboutMealImpactValue(item.aboutMeal, constants.CARBS_ATTR_ID) *
            kcalMultiplier.carbs;
          individualTotals.fats +=
            getAboutMealImpactValue(item.aboutMeal, constants.FATS_ATTR_ID) *
            kcalMultiplier.fats;
          individualTotals.sugar += getAboutMealImpactValue(
            item.aboutMeal,
            constants.SUGAR_ATTR_ID
          );
          individualTotals.calories += getAboutMealImpactValue(
            item.aboutMeal,
            constants.CALORIES_ATTR_ID
          );
        }
      });

      const impact = {
        protein: {},
        carbs: {},
        fats: {},
      };

      const currentProteinVal =
        getAboutMealImpactValue(hpbcImpact, constants.PROTEIN_ATTR_ID) *
        kcalMultiplier.protein;
      const currentCarbs =
        getAboutMealImpactValue(hpbcImpact, constants.CARBS_ATTR_ID) *
        kcalMultiplier.carbs;
      const currentFats =
        getAboutMealImpactValue(hpbcImpact, constants.FATS_ATTR_ID) *
        kcalMultiplier.fats;
      const currentSugar = getAboutMealImpactValue(
        hpbcImpact,
        constants.SUGAR_ATTR_ID
      );

      individualTotals.protein += currentProteinVal;
      individualTotals.carbs += currentCarbs;
      individualTotals.fats += currentFats;
      individualTotals.sugar += currentSugar;

      const total = individualTotals.calories + calories.numericValue;

      impact.protein = {
        value:
          total > 0 ? roundMealValue((currentProteinVal / total) * 100.0) : 0,
        sign: "+",
      };
      impact.carbs = {
        value: total > 0 ? roundMealValue((currentCarbs / total) * 100.0) : 0,
        sign: "+",
      };
      impact.fats = {
        value: total > 0 ? roundMealValue((currentFats / total) * 100.0) : 0,
        sign: "+",
      };

      const tmpChartData = [
        {
          label: t("appText.dietDiary.protein"),
          value:
            total > 0
              ? roundMealValue((individualTotals.protein / total) * 100.0)
              : 0,
          impact: impact.protein,
          color: getMacronutrientColor(
            constants.PROTEIN_ATTR_ID,
            theme,
            studyId
          ),
          unit: "%",
        },
        {
          label: t("appText.dietDiary.carbohydrates"),
          value:
            total > 0
              ? roundMealValue((individualTotals.carbs / total) * 100.0)
              : 0,
          impact: impact.carbs,
          color: getMacronutrientColor(constants.CARBS_ATTR_ID, theme, studyId),
          unit: "%",
        },
        {
          label: t("appText.dietDiary.fats"),
          value:
            total > 0
              ? roundMealValue((individualTotals.fats / total) * 100.0)
              : 0,
          impact: impact.fats,
          color: getMacronutrientColor(constants.FATS_ATTR_ID, theme, studyId),
          unit: "%",
        },
        {
          label: `${t("appText.dietDiary.totalSugarToday")}:`,
          value: roundMealValue(individualTotals.sugar),
          hideBar: true,
          unit: "g",
        },
      ];

      setHpbcVals(tmpChartData);
    }
  }, [
    existingDayMeals,
    getAboutMealImpactValue,
    constants,
    setHpbcVals,
    hpbcImpact,
    getMacronutrientColor,
    mealId,
    theme,
    studyId,
    t,
    calories.numericValue,
  ]);

  useEffect(() => {
    if (startDate) {
      fetchMealsByDate(
        moment(startDate).format("YYYY/MM/DDT00:00:00"),
        participantId
      ).catch((error) => {
        sendNotification(error, ToastType.ERROR);
      });
    }
  }, [startDate, fetchMealsByDate, participantId]);

  useEffect(() => {
    return () => {
      resetStates();
    };
  }, [resetStates]);

  return (
    <>
      <NutritionItem
        value={calories.value}
        title={`${t("appText.dietDiary.totalCalories")}:`}
        backgroundColor={props.theme?.lightShadeMagenta}
        titleColor={props.theme?.darkPurple}
      />
      {calories.numericValue > 0 && (
        <StyledPieChart>
          <PieChart
            customKey={pieChartKey || "pie-chart"}
            series={pieChartSeries.filter((item) => item.frequency > 0)}
            width={380}
            height={300}
          />
        </StyledPieChart>
      )}
      {hpbcImpact && (
        <StyledHPBC>
          <HorizontalProgressBarChart
            title={getMealImpactTitle()}
            data={hpbcVals}
          />
        </StyledHPBC>
      )}
    </>
  );
};

export default dynamic(
  "dietDiaryChartsReducer",
  subspaced({
    // We map extra state because we might need to acess other stores for data, subspace is self contained.
    mapExtraState: (state) => ({ loginReducer: state.loginReducer }),
  }),
  attachReducer(dietDiaryChartsReducer)
)(
  connect(mapStateToProps, mapDispatchToProps)(withTheme(SmartDietDiaryCharts))
);
